import { withAsyncDeadline } from './async-deadline';

export type InventoryBirthAnswer = { ok: true } | { ok: false; error: string };
type BirthWait = Promise<InventoryBirthAnswer> & { saved?: () => void };
export const INVENTORY_BIRTH_WAIT_WORDS = 'That inventory save could not be confirmed. Wait for delivery to resume, then try this action again.';

declare global {
  interface Window { ftPendingInventoryBirths?: Map<string, Promise<InventoryBirthAnswer>> }
}

/** The add and equip bundles share row identities, never a module-owned queue. */
export function holdInventoryBirth(ids: readonly string[], saved?: () => void): (answer: InventoryBirthAnswer, parked?: boolean) => void {
  const pending = window.ftPendingInventoryBirths ??= new Map<string, Promise<InventoryBirthAnswer>>();
  const keys = ids.map(id => id.trim().toUpperCase());
  let resolve!: (answer: InventoryBirthAnswer) => void;
  const promise: BirthWait = new Promise<InventoryBirthAnswer>(done => { resolve = done; });
  for (const id of keys) pending.set(id, promise);
  const settle = (answer: InventoryBirthAnswer, parked = false): void => {
    // A parked save may still paint/complete later. Until it resumes, a new click
    // must see the resolved refusal rather than mistake that row for persisted.
    if (!parked) for (const id of keys) if (pending.get(id) === promise) pending.delete(id);
    if (answer.ok) saved?.();
    resolve(answer);
  };
  promise.saved = () => settle({ ok: true });
  return settle;
}

/** No promise or actor-wide barrier for an already persisted row. */
export function pendingInventoryBirth(id: string): Promise<InventoryBirthAnswer> | undefined {
  return window.ftPendingInventoryBirths?.get(id.trim().toUpperCase());
}

/** Cancel only this consumer's wait, including time spent behind a sending head. */
export async function awaitInventoryBirth(id: string): Promise<InventoryBirthAnswer> {
  const birth = pendingInventoryBirth(id);
  if (!birth) return { ok: true };
  try { return await withAsyncDeadline(() => birth, 20_000); }
  catch { return { ok: false, error: 'inventory-birth:save-unconfirmed' }; }
}

/** Only server recovery/relay evidence may release an unconfirmed birth. */
export function confirmInventoryBirth(id: string): void {
  (pendingInventoryBirth(id) as BirthWait | undefined)?.saved?.();
}
